import { useKeycloak } from "@react-keycloak/web";
import { create } from "zustand";
import { getFloorsBySiteId, getRoomsBySiteIdAndFloorId } from "../api/locations";

export const useLocationsStore = create<LocationsStore>((set) => {
  return {
    floors: {},
    roomsByFloor: {},
    roomsBySite: {},
    rows: {},
    loadingFloors: {},
    loadingRooms: {},
    setFloors: (floorsData: FloorInterface[], siteId: string) => {
      set((state) => ({
        ...state,
        floors: {
          ...state.floors,
          [siteId]: floorsData
        },
        loadingFloors: {
          ...state.loadingFloors,
          [siteId]: false
        }
      }))
    },
    setRooms: (roomData: RoomInterface[], siteId: string, floorId: string) => {
      set((state) => ({
        ...state,
        roomsByFloor: {
          ...state.roomsByFloor,
          [floorId]: roomData
        },
        roomsBySite: {
          ...state.roomsBySite,
          [siteId]: roomData
        },
        loadingRooms: {
          ...state.loadingRooms,
          [floorId]: false
        }
      }))
    },
    setRows: (rowData: RowInterface[], roomId: string) => {
      set((state) => ({
        ...state,
        rows: {
          ...state.rows,
          [roomId]: rowData
        }
      }))
    },
    setLoadingFloors: (siteId: string, loading: boolean) => {
      set((state) => ({
        ...state,
        loadingFloors: {
          ...state.loadingFloors,
          [siteId]: loading
        }
      }))
    },
    setLoadingRooms: (floorId: string, loading: boolean) => {
      set((state) => ({
        ...state,
        loadingRooms: {
          ...state.loadingRooms,
          [floorId]: loading
        }
      }))
    }
  };
});

export const useLocationsPage = () => {
  const { keycloak: { token } } = useKeycloak();

  return {
    loadFloors: async (projectId: string, siteId: string) => {
      if (!token || !projectId || !siteId) return;
      
      const state = useLocationsStore.getState();
      
      // Check if already loaded or loading
      if (state.floors[siteId] || state.loadingFloors[siteId]) {
        console.log('Floors already loaded or loading for site:', siteId);
        return;
      }
      
      // Mark as loading
      state.setLoadingFloors(siteId, true);
      
      try {
        console.log('API call: Loading floors for project:', projectId, 'site:', siteId);
        const floors = await getFloorsBySiteId(token, projectId, siteId);
        state.setFloors(floors, siteId);
        console.log('API response: Floors loaded for site:', siteId, floors.length, 'floors');
      } catch (error) {
        console.error('Error loading floors for site:', siteId, error);
        state.setLoadingFloors(siteId, false);
      }
    },

    loadRooms: async (projectId: string, siteId: string, floorId: string, forceReload: boolean = false) => {
      if (!token || !projectId || !siteId || !floorId) {
        console.log('Missing parameters for loadRooms:', { token: !!token, projectId, siteId, floorId });
        return;
      }

      const state = useLocationsStore.getState();
      
      // Check if already loading (but allow reloading if not currently loading)
      if (state.loadingRooms[floorId]) {
        console.log('Rooms already loading for floor:', floorId);
        return;
      }
      
      // Allow reloading if needed, just skip if already loaded AND has data (unless forceReload is true)
      if (!forceReload && state.roomsByFloor[floorId] && state.roomsByFloor[floorId].length > 0) {
        console.log('Rooms already loaded for floor:', floorId, 'count:', state.roomsByFloor[floorId].length);
        return;
      }
      
      // Mark as loading
      state.setLoadingRooms(floorId, true);
      
      try {
        console.log('API call: Loading rooms for project:', projectId, 'site:', siteId, 'floor:', floorId, 'forceReload:', forceReload);
        const roomsData = await getRoomsBySiteIdAndFloorId(token, projectId, siteId, floorId);
        const safeRoomsData = Array.isArray(roomsData) ? roomsData : [];
        state.setRooms(safeRoomsData, siteId, floorId);
        console.log('API response: Rooms loaded for floor:', floorId, safeRoomsData.length, 'rooms');
      } catch (error) {
        console.error('Error loading rooms for floor:', floorId, error);
        // Set empty array on error to prevent undefined access
        state.setRooms([], siteId, floorId);
        state.setLoadingRooms(floorId, false);
      }
    },

    forceReloadRooms: async (projectId: string, siteId: string, floorId: string) => {
      if (!token || !projectId || !siteId || !floorId) {
        console.log('Missing parameters for forceReloadRooms:', { token: !!token, projectId, siteId, floorId });
        return;
      }

      const state = useLocationsStore.getState();
      
      // Clear existing data first
      state.setRooms([], siteId, floorId);
      
      // Mark as loading
      state.setLoadingRooms(floorId, true);
      
      try {
        console.log('API call: Force reloading rooms for project:', projectId, 'site:', siteId, 'floor:', floorId);
        const roomsData = await getRoomsBySiteIdAndFloorId(token, projectId, siteId, floorId);
        const safeRoomsData = Array.isArray(roomsData) ? roomsData : [];
        state.setRooms(safeRoomsData, siteId, floorId);
        console.log('API response: Rooms force reloaded for floor:', floorId, safeRoomsData.length, 'rooms');
      } catch (error) {
        console.error('Error force reloading rooms for floor:', floorId, error);
        // Set empty array on error to prevent undefined access
        state.setRooms([], siteId, floorId);
        state.setLoadingRooms(floorId, false);
      }
    },
  }
}
